import { useUpdatePatientMutation } from '@pm/graphql';
import { usePatientProfileContext } from '../../providers/PatientProfileProvider';
import { useProfileContext } from '../../providers/ProfileProvider';
import { BRAND_NAME } from '../../types';

enum Role {
  Patient = 'PATIENT',
  Clinician = 'CLINICIAN',
  CX = 'CX',
  MOA = 'MOA',
  SuperUser = 'ADMIN',
  Pharmacy = 'PHARMACY',
}

export const useProfile = () => {
  const context = useProfileContext();

  if (!context) {
    throw 'This hook must be used inside a ProfileProvider';
  }

  const { profile, refetch, loading } = context;

  const providerRoles = [
    Role.Clinician,
    Role.CX,
    Role.MOA,
    Role.SuperUser,
    Role.Pharmacy,
  ];

  return {
    profile: profile,
    isPatient: profile?.role.code === Role.Patient,
    isClinician: profile?.role.code === Role.Clinician,
    isCx: profile?.role.code === Role.CX,
    isMoa: profile?.role.code === Role.MOA,
    isSuperuser: profile?.role.code === Role.SuperUser,
    isPharmacy: profile?.role.code === Role.Pharmacy,
    get isProvider() {
      return providerRoles.includes(profile?.role.code as Role);
    },
    get isNonClinicianProvider() {
      return this.isProvider && !this.isClinician;
    },
    userId: profile?.id,
    roleId: profile?.role.id,
    name: `${profile?.firstName} ${profile?.lastName}`,
    chosenName: profile?.preferredName,
    refetch,
    loading,
  };
};

export const usePatientProfile = () => {
  const context = usePatientProfileContext();

  if (!context) {
    throw 'This hook must be used inside a PatientProfileProvider';
  }

  const { profile, refetch, loading } = context;
  const firstName = profile?.firstName ?? '';
  const lastName = profile?.lastName ?? '';
  return {
    profile: profile,
    userId: profile?.id,
    name: `${firstName} ${lastName}`,
    province: profile?.profile?.province,
    country: profile?.profile?.province?.country,
    inFranklinCounty: profile?.profile?.inFranklinCounty,
    chosenName: profile?.preferredName,
    isUsaPatient:
      // FIXME: Check country instead of brand membership
      profile?.currentBrandMembership?.name?.toLowerCase() ===
      // eslint-disable-next-line @pm/architecture/no-brand-logic
      BRAND_NAME.FreddieUSA,
    currentBrandName: profile?.currentBrandMembership?.name,
    currentBrandId: profile?.currentBrandMembership?.brand?.id,
    refetch,
    loading,
  };
};

export const useUpdatePatientProfile: typeof useUpdatePatientMutation = (
  baseOptions,
) => useUpdatePatientMutation(baseOptions);
